// Import Skill types for internal use
import type { SkillDefinition, SkillLevel } from "./skills";

// Re-export Skill-Definitionen aus skills.ts
export {
  SKILL_DEFINITIONS,
  EMOJI_TO_SKILL,
  SKILL_TO_EMOJI,
  SKILL_LEVEL_LABELS,
  SKILL_LEVEL_COLORS,
  getSkillById,
  getSkillByEmoji,
} from "./skills";
export type { SkillDefinition, SkillLevel } from "./skills";

// Board Definition Interface
export interface BoardDefinition {
  id: string;              // z.B. "board-roadmap-2026-q1"
  name: string;            // z.B. "Roadmap 2026 Q1"
  startDate: string;       // ISO-Datum, ersetzt BOARD_START_DATE pro Board
  createdAt: string;       // ISO-Timestamp
  updatedAt: string;       // ISO-Timestamp
  employeeIds: string[];   // Aktive Mitarbeiter-Teilmenge fuer dieses Board
}

// Board Requirement (pro Board, nicht global)
export interface BoardRequirement {
  text: string;
  type: 'hard' | 'soft';
  description: string;
}

// Task Category Definition
export interface TaskCategory {
  id: string;
  name: string;
  color: string;
}

// Column Definition (replaces plain string columns)
export interface ColumnDefinition {
  name: string;
  defaultCategoryIds: string[];
}

// Team Definition Interface
export interface TeamDefinition {
  id: string;
  name: string;
  color: string;
}

// TeamId ist jetzt dynamisch (string), aber wir behalten den Typ für Kompatibilität
export type TeamId = string;

// Default Teams (werden im Store verwendet)
export const DEFAULT_TEAMS: TeamDefinition[] = [];

// TEAM_DEFINITIONS wird dynamisch aus dem Store geladen
// Diese Variable wird zur Laufzeit aktualisiert
export let TEAM_DEFINITIONS: Record<string, { name: string; color: string }> = {};

// Hilfsfunktion zum Aktualisieren der TEAM_DEFINITIONS
export function updateTeamDefinitions(teams: TeamDefinition[]): void {
  TEAM_DEFINITIONS = {};
  for (const team of teams) {
    TEAM_DEFINITIONS[team.id] = { name: team.name, color: team.color };
  }
}

export interface Employee {
  id: string;
  name: string;
  color: string;
  team: TeamId;
  skills?: string; // Emoji-String, z.B. "🐘🐘🐘👷👷"
}

// PERT-Schätzung mit drei Werten
export interface PertEstimate {
  o: number; // Optimistisch (Best Case)
  m: number; // Most Likely (Wahrscheinlichster Wert)
  p: number; // Pessimistisch (Worst Case)
}

// Modus für Schätzungen: Task-weit oder pro Mitarbeiter
export type EstimateMode = 'default' | 'individual';

const DAYS_PER_WEEK = 5;

// Berechnet den PERT-Erwartungswert in Tagen: E = (O + 4M + P) / 6
export function pertExpectedDays(est: PertEstimate): number {
  return (est.o + 4 * est.m + est.p) / 6;
}

// Berechnet den PERT-Erwartungswert in Wochen
export function pertExpectedValue(est: PertEstimate): number {
  return pertExpectedDays(est) / DAYS_PER_WEEK;
}

// Berechnet die PERT-Standardabweichung in Tagen: σ = (P - O) / 6
export function pertStandardDeviationDays(est: PertEstimate): number {
  return (est.p - est.o) / 6;
}

// Berechnet die PERT-Standardabweichung in Wochen
export function pertStandardDeviation(est: PertEstimate): number {
  return pertStandardDeviationDays(est) / DAYS_PER_WEEK;
}

// Globaler Task (ohne board-spezifische Felder column)
export interface GlobalTask {
  id: string;
  title: string;
  description?: unknown[];
  consequence?: unknown[];
  startDate: string; // ISO-Datum (z.B. "2026-01-12")
  estimateMode: EstimateMode;
  defaultEstimate?: PertEstimate;
  estimates: Record<string, PertEstimate | null>;
  assigneeIds: string[];
  categoryIds: string[];
  requiredSkills?: string | SkillNode;
  requirements_hard?: TaskRequirement[];
  requirements_soft?: TaskRequirement[];
}

// Pro-Board Sicht auf einen Task
export interface BoardTaskView {
  column: string;
}

// Alle Board-Views eines Boards
export interface BoardTaskViewsData {
  [taskId: string]: BoardTaskView;
}

// Materialisierte Sicht: GlobalTask + Board-spezifische Felder
export interface Task {
  id: string;
  title: string;
  description?: unknown[];
  consequence?: unknown[];
  column: string;
  startDate: string; // ISO-Datum (z.B. "2026-01-12")
  estimateMode: EstimateMode;
  defaultEstimate?: PertEstimate;
  estimates: Record<string, PertEstimate | null>;
  assigneeIds: string[];
  categoryIds: string[];
  requiredSkills?: string | SkillNode;
  requirements_hard?: TaskRequirement[];
  requirements_soft?: TaskRequirement[];
}

// Requirement für einen Task
export interface TaskRequirement {
  text: string; // Name/Identifier des Requirements (für Gruppierung)
  description?: string; // Optionale Task-spezifische Beschreibung
}

// Globales Requirement (nicht an einen Task gebunden)
export interface GlobalRequirement {
  text: string; // Name/Identifier des Requirements
  type: 'hard' | 'soft';
  description: string; // Beschreibung warum dieses Requirement benötigt wird
}

export interface BoardData {
  columns?: string[]; // Deprecated: Columns werden jetzt aus dem columnsStore geladen
  startDate: string; // ISO-Datum des Board-Starts (z.B. "2026-01-12")
  totalWeeks: number;
  employees: Employee[];
  tasks: Task[];
  globalRequirements?: GlobalRequirement[]; // Globale Requirements (nicht an Tasks gebunden)
}

// Speicherformat für Export/Import (nur veränderbare Daten für bestehende Tasks)
export interface TaskState {
  id: string;
  startDate: string;
  assigneeIds: string[];
}

// Serialisierbare Task-Daten für Export/Import
export interface SerializableTask {
  id: string;
  title: string;
  description?: string | unknown[];
  consequence?: string | unknown[];
  column: string;
  startDate: string;
  estimateMode: EstimateMode; // 'default' = Task-Schätzung, 'individual' = pro Mitarbeiter
  defaultEstimate?: PertEstimate; // Task-weite Schätzung (nur bei estimateMode: 'default')
  estimates: Record<string, PertEstimate | null>;
  assigneeIds: string[];
  categoryIds: string[];
  requiredSkills?: string; // Nur Emoji-String für neue Tasks (SkillNode wird nicht unterstützt)
  requirements_hard?: TaskRequirement[];
  requirements_soft?: TaskRequirement[];
}

export interface SavedView {
  version: 1;
  tasks: TaskState[];
  newTasks?: SerializableTask[]; // Backwards compatibility: nur neu erstellte Tasks
  allTasks?: SerializableTask[]; // Vollständige Daten für ALLE Tasks (neues Format)
  skills?: SkillDefinition[];
  employees?: Employee[];
  teams?: TeamDefinition[];
  taskCategories?: TaskCategory[];
  columns?: string[] | ColumnDefinition[]; // Columns (legacy string[] or new ColumnDefinition[])
  globalRequirements?: GlobalRequirement[]; // Globale Requirements
  taskIdCounter?: number;
}

// Skill Engine Types - importiert aus skills.ts

export interface EmployeeSkills {
  employeeId: string;
  skills: Record<string, SkillLevel>; // skillId -> level
}

export interface ProjectRequirement {
  skillId: string;
  minLevel: SkillLevel;
}

// ==========================================
// Skill Requirement DSL
// ==========================================

export type SkillNodeType = 'skill' | 'or' | 'all';

export interface SkillNode {
  type: SkillNodeType;
  skills?: string;           // Bei type: 'skill' - der Emoji-String
  effort?: number;           // Aufwands-Multiplikator (default: 1.0)
  children?: SkillNode[];    // Bei type: 'or' | 'all'
}

// Ergebnis einer Skill-Prüfung
export interface SkillMatchResult {
  matches: boolean;
  effort: number;            // Resultierender Aufwands-Multiplikator
  matchedPath?: string;      // Debug: Welcher Pfad wurde gematcht
}

// ==========================================
// ADR (Architecture Decision Records)
// ==========================================

export type AdrStatus = 'draft' | 'proposed' | 'accepted' | 'deprecated' | 'superseded';
export type AdrImplementationStatus = 'pending' | 'in_progress' | 'done' | 'abandoned';
export type AdrRelationType = 'supersedes' | 'superseded_by' | 'amends' | 'amended_by' | 'relates_to';
export type AdrPersonRole = 'author' | 'responsible' | 'reviewer';

export interface AdrRelation {
  targetAdrId: string;
  type: AdrRelationType;
}

export interface AdrPerson {
  employeeId: string;
  role: AdrPersonRole;
}

export interface AdrCommentNode {
  id: string;
  authorEmployeeId: string;
  text: string;
  resolved: boolean;
  children: AdrCommentNode[];
  createdAt: string;
}

export interface ADR {
  id: string;
  title: string;
  scope:
    | { type: 'global' }
    | { type: 'board'; boardId: string }
    | { type: 'canvas'; boardId: string; treeId: string };

  context: unknown[];
  decision: unknown[];
  consequences: unknown[];

  status: AdrStatus;
  implementationStatus: AdrImplementationStatus;

  relations: AdrRelation[];
  people: AdrPerson[];
  comments: AdrCommentNode[];

  createdAt: string;
  updatedAt: string;
}

// ==========================================
// Resource Catalog
// ==========================================

export type ResourceFieldType = 'currency' | 'number' | 'text' | 'percentage' | 'duration';
export type ResourceAggregation = 'sum' | 'avg' | 'min' | 'max' | 'none';

export interface ResourceField {
  id: string;
  key: string;
  value: string | number;
  type: ResourceFieldType;
  unit?: string;
  interval?: 'monthly' | 'yearly' | 'hourly';
  aggregate: ResourceAggregation;
  showOnBadge: boolean;
}

export interface ResourceDefinition {
  id: string;
  name: string;
  category: string;
  scope:
    | { type: 'global' }
    | { type: 'board'; boardId: string }
    | { type: 'canvas'; boardId: string; treeId: string };
  fields: ResourceField[];
  color?: string;
  createdAt: string;
  updatedAt: string;
}
